import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BookMarked,
  CalendarPlus,
  CheckCircle2,
  FileUp,
  Loader,
  MessageSquareText,
  Save,
  Settings2,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { TellrawComposer, EMPTY_COMPOSER } from '../components/scheduledcommands/tellraw/TellrawComposer';
import type { ComposerValue } from '../components/scheduledcommands/tellraw/TellrawComposer';
import { TellrawPreview } from '../components/scheduledcommands/tellraw/TellrawPreview';
import { scheduledCommandsApi } from '../services/scheduledCommandsApi';
import type { TellrawLibraryEntry, TellrawLibraryInput, TellrawSource } from '../types/scheduledCommands';
import { getScheduledCommandPerms } from '../utils/scheduledCommandPermissions';
import { compileTellraw, parseTellraw, validateMessage } from '../utils/tellraw';
import type { ParseResult } from '../utils/tellraw/types';
import { cn } from '../utils/cn';

interface EntryForm {
  name: string;
  notes: string;
  composer: ComposerValue;
  /** Whether the message came from an import, until the assistant or a hand edit takes over. */
  imported: boolean;
}

const EMPTY_ENTRY: EntryForm = { name: '', notes: '', composer: EMPTY_COMPOSER, imported: false };
const MAX_IMPORT_BYTES = 256 * 1024;

const fromEntry = (entry: TellrawLibraryEntry): EntryForm => ({
  name: entry.name,
  notes: entry.notes ?? '',
  composer: { message: entry.message, compat: entry.compat, selector: '@a', conversation: entry.conversation ?? [] },
  imported: entry.source === 'import',
});

function toInput(form: EntryForm): TellrawLibraryInput {
  const { message, compat, conversation } = form.composer;
  const source: TellrawSource = conversation.length ? 'assistant' : form.imported ? 'import' : 'manual';
  return {
    name: form.name.trim(),
    notes: form.notes.trim() || null,
    message,
    compat,
    command: compileTellraw(message, compat).command,
    source,
    conversation: conversation.length ? conversation.slice(-40) : null,
  };
}

/**
 * One saved tellraw: written with the assistant, imported from an existing
 * command, or refined from an earlier save. The conversation is kept with it,
 * so a follow-up weeks later still knows what the message was for.
 */
export function TellrawLibraryEntryView() {
  const { entryId = 'new' } = useParams();
  const [params] = useSearchParams();
  return <LibraryEntryEditor key={`${entryId}|${params.get('import') ?? ''}`} entryId={entryId} startWithImport={params.get('import') === '1'} />;
}

function LibraryEntryEditor({ entryId, startWithImport }: { entryId: string; startWithImport: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getScheduledCommandPerms(identity, isDev), [identity, isDev]);
  const isNew = entryId === 'new';

  const [entry, setEntry] = useState<TellrawLibraryEntry | null>(null);
  const [form, setForm] = useState<EntryForm>(EMPTY_ENTRY);
  const [baseline, setBaseline] = useState<EntryForm>(EMPTY_ENTRY);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>((location.state as { saved?: boolean } | null)?.saved ? 'Saved to the library.' : null);
  const [deleting, setDeleting] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [importOpen, setImportOpen] = useState(startWithImport);
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState<ParseResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useDocumentTitle(isNew ? 'New tellraw' : entry?.name ?? 'Tellraw');

  useEffect(() => {
    if (isNew) return;
    let live = true;
    scheduledCommandsApi
      .libraryEntry(entryId)
      .then((loaded) => {
        if (!live) return;
        setEntry(loaded);
        setForm(fromEntry(loaded));
        setBaseline(fromEntry(loaded));
      })
      .catch((e: unknown) => live && setLoadError(e instanceof Error ? e.message : 'This entry could not be loaded.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [entryId, isNew]);

  const input = useMemo(() => toInput(form), [form]);
  const dirty = useMemo(() => JSON.stringify(input) !== JSON.stringify(toInput(baseline)), [input, baseline]);
  const issues = useMemo(() => validateMessage(form.composer.message), [form.composer.message]);
  const canSave = perms.canWrite && !!form.name.trim() && form.composer.message.runs.length > 0 && issues.length === 0 && (isNew || dirty);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = (patch: Partial<EntryForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
    setNote(null);
  };

  const checkImport = (text: string) => {
    setImportText(text);
    setImportResult(text.trim() ? parseTellraw(text) : null);
  };

  const readFile = async (file: File) => {
    if (file.size > MAX_IMPORT_BYTES) {
      setImportResult({ ok: false, error: 'That file is larger than any tellraw should be (over 256 KB).' });
      return;
    }
    checkImport(await file.text());
  };

  const applyImport = () => {
    if (!importResult?.ok) return;
    // Keep an imported message looking as it did: hex colours mean it was written
    // for 1.16 or newer, so stepping them for 1.15 would quietly change it.
    const usesHex = importResult.message.runs.some(
      (run) => !('raw' in run) && ((run.color?.startsWith('#') ?? false) || (run.gradient?.length ?? 0) > 0),
    );
    update({
      composer: {
        ...form.composer,
        message: importResult.message,
        conversation: [],
        compat:
          usesHex && form.composer.compat.legacy
            ? form.composer.compat.modern || form.composer.compat.current
              ? { ...form.composer.compat, legacy: false }
              : { legacy: false, modern: true, current: true }
            : form.composer.compat,
      },
      imported: true,
      name: form.name.trim() ? form.name : 'Imported tellraw',
    });
    setImportOpen(false);
    setImportText('');
    setImportResult(null);
    setNote(
      importResult.editable
        ? 'Imported. Check the preview, then save it or ask the assistant to change it.'
        : 'Imported. Parts of it only the server can fill in, so it is kept exactly as written and the assistant is not offered.',
    );
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await scheduledCommandsApi.saveToLibrary(input);
        navigate(`/scheduled-commands/library/${encodeURIComponent(created.id)}`, { replace: true, state: { saved: true } });
        return;
      }
      const updated = await scheduledCommandsApi.updateLibraryEntry(entryId, input);
      setEntry(updated);
      const next = { ...fromEntry(updated), composer: { ...form.composer, compat: updated.compat } };
      setBaseline(next);
      setForm(next);
      setNote('Saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await scheduledCommandsApi.deleteLibraryEntry(entryId);
      navigate('/scheduled-commands?tab=library', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It could not be deleted.');
      setDeleting(false);
    }
  };

  const back = () => (dirty ? setLeaving(true) : navigate('/scheduled-commands?tab=library'));

  if (!perms.canView) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Tellraw library" icon={BookMarked} backTo="/scheduled-commands?tab=library" backLabel="Tellraw library" />
        <EmptyState
          tone="warning"
          icon={ShieldCheck}
          title="You don't have permission to view the tellraw library"
          description={<>Ask an admin for the <code className="font-mono text-xs">argus.scheduledcommands.read</code> permission.</>}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Loading…" icon={BookMarked} backTo="/scheduled-commands?tab=library" backLabel="Tellraw library" />
        <SkeletonLoader className="h-32" />
        <SkeletonLoader className="h-96" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Tellraw" icon={BookMarked} backTo="/scheduled-commands?tab=library" backLabel="Tellraw library" />
        <EmptyState tone="danger" icon={AlertCircle} title="This entry could not be opened" description={loadError} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Network Config"
        title={form.name.trim() || (isNew ? 'New tellraw' : entry?.name ?? 'Tellraw')}
        icon={BookMarked}
        onBack={back}
        backLabel="Tellraw library"
        description={
          isNew
            ? 'Write it with the assistant, or import an existing command, then save it for any schedule to use.'
            : entry && `Saved by ${entry.updated_by_name ?? entry.created_by_name ?? 'someone'}.`
        }
        actions={
          perms.canWrite && (
            <div className="flex items-center gap-2">
              {!isNew && entry && (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(`/scheduled-commands/new?library=${encodeURIComponent(entry.id)}`)}
                    className="glass-button"
                    disabled={dirty}
                    title={dirty ? 'Save first, so the schedule gets this version' : 'Start a new schedule with this message'}
                  >
                    <CalendarPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">Schedule it</span>
                  </button>
                  <button type="button" onClick={() => setDeleting(true)} className="btn-icon text-red-400 hover:text-red-300" title="Delete" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
              <button type="button" onClick={() => void save()} className="btn-primary" disabled={!canSave || saving}>
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isNew ? 'Save to library' : 'Save changes'}
              </button>
            </div>
          )
        }
      />

      {note && (
        <div className="alert alert-success" role="status">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span className="flex-1">{note}</span>
          <button type="button" onClick={() => setNote(null)} className="btn-ghost shrink-0">Dismiss</button>
        </div>
      )}
      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <section className="glass-card p-4 md:p-5 min-w-0">
          <SectionHeader
            title="Message"
            icon={MessageSquareText}
            actions={
              perms.canWrite && !importOpen && (
                <button type="button" onClick={() => setImportOpen(true)} className="btn-ghost btn-xs">
                  <Upload className="w-3.5 h-3.5" />
                  Import
                </button>
              )
            }
          />

          {importOpen && perms.canWrite && (
            <div className="subcard p-3 mb-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-glass-300">Paste a tellraw command or its JSON, or open a .json or .txt file.</p>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".json,.txt,application/json,text/plain"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void readFile(file);
                      e.target.value = '';
                    }}
                  />
                  <button type="button" onClick={() => fileRef.current?.click()} className="glass-button glass-button-sm">
                    <FileUp className="w-4 h-4" />
                    Open a file
                  </button>
                </div>
              </div>
              <textarea
                className={cn('glass-input w-full font-mono text-xs leading-5 min-h-[9rem]', importResult && !importResult.ok && 'border-red-500/50')}
                value={importText}
                onChange={(e) => checkImport(e.target.value)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) void readFile(file);
                }}
                placeholder={'tellraw @a ["",{"text":"Welcome to ","color":"gray"},{"text":"CDU","color":"gold","bold":true}]'}
                spellCheck={false}
                aria-label="Tellraw to import"
              />
              {importResult && !importResult.ok && (
                <p className="flex items-start gap-1.5 text-xs text-red-400" role="alert">
                  <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                  {importResult.error}
                  {importResult.line ? ` (line ${importResult.line}, column ${importResult.column ?? 1})` : ''}
                </p>
              )}
              {importResult?.ok && (
                <div className="space-y-2">
                  <TellrawPreview component={importResult.component} />
                  {!importResult.editable && (
                    <p className="text-xs text-glass-400">
                      It contains parts only the server can fill in (a score, a selector or a translation). It will be kept exactly as written.
                    </p>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setImportOpen(false);
                    setImportText('');
                    setImportResult(null);
                  }}
                  className="glass-button-secondary"
                >
                  Cancel
                </button>
                <button type="button" onClick={applyImport} className="glass-button" disabled={!importResult?.ok}>
                  {form.composer.message.runs.length ? 'Replace the message' : 'Use this'}
                </button>
              </div>
            </div>
          )}

          <TellrawComposer
            value={form.composer}
            onChange={(composer) => update({ composer, imported: form.imported && composer.conversation.length === 0 && composer.message === form.composer.message })}
            canWrite={perms.canWrite}
            defaultPlayer={identity?.raw?.custom?.username ?? null}
          />
        </section>

        <aside className="space-y-6 xl:sticky xl:top-4">
          <section className="glass-card p-4 md:p-5 space-y-4">
            <SectionHeader title="Details" icon={Settings2} className="mb-0" />
            <div>
              <label htmlFor="entry-name" className="block text-xs font-medium text-glass-400 mb-1.5">Name</label>
              <input
                id="entry-name"
                className="glass-input w-full"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                maxLength={100}
                placeholder="e.g. Store sale, gold"
                disabled={!perms.canWrite}
              />
            </div>
            <div>
              <label htmlFor="entry-notes" className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
              <textarea
                id="entry-notes"
                className="glass-input w-full min-h-[5rem]"
                value={form.notes}
                onChange={(e) => update({ notes: e.target.value })}
                maxLength={1000}
                placeholder="When to use it, what to change before reusing it…"
                disabled={!perms.canWrite}
              />
            </div>
            {perms.canWrite && !canSave && (isNew || dirty) && (
              <p className="text-xs text-glass-500">
                {!form.name.trim()
                  ? 'Give it a name to save it.'
                  : form.composer.message.runs.length === 0
                    ? 'Write or import a message to save it.'
                    : issues[0]?.message}
              </p>
            )}
          </section>
        </aside>
      </div>

      <ConfirmationModal
        isOpen={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => void remove()}
        title={`Delete ${entry?.name ?? 'this entry'}?`}
        message="It leaves the library. Schedules that already use this message keep their own copy and carry on."
        confirmText="Delete"
      />
      <ConfirmationModal
        isOpen={leaving}
        onClose={() => setLeaving(false)}
        onConfirm={() => navigate('/scheduled-commands?tab=library')}
        title="Leave without saving?"
        message="The changes to this tellraw are not saved and will be lost."
        confirmText="Leave"
      />
    </div>
  );
}
